import { Check, CheckHourly } from '../types';
import { parseServerDate } from './datetime';

export interface SeriesPoint {
  /** Epoch milliseconds — a real number, so the axis can be time-scaled. */
  t: number;
  responseTime: number | null;
}

/**
 * Build a response-time series that tells the truth about time.
 *
 * The old chart stringified each timestamp and used it as a category label, so
 * points were spaced evenly no matter how far apart they actually were — a
 * 40-minute outage drew the same width as a 30-second gap. Here the x value is
 * the real timestamp, and a missed window is emitted as an explicit `null` so
 * the line breaks instead of drawing straight through the outage.
 */
export function buildResponseSeries(checks: Check[], intervalSeconds: number): SeriesPoint[] {
  const samples = checks
    .filter(c => c.response_time !== null)
    .map(c => ({ t: parseServerDate(c.timestamp)?.getTime() ?? NaN, value: c.response_time as number }));

  // Allow some scheduler jitter before calling it a gap.
  return withGaps(samples, Math.max(intervalSeconds * 2.5, 60) * 1000);
}

/**
 * The same series shape from the hourly rollup, for windows too long for a
 * raw sample to cover. An hour with no response (all checks failed) has a
 * NULL average, and an hour with no checks has no row at all; both must
 * break the line, so the gap threshold sits between one and two hours.
 */
export function buildHourlyResponseSeries(stats: CheckHourly[]): SeriesPoint[] {
  const samples = stats
    .filter(h => h.avg_response_time !== null)
    .map(h => ({ t: parseServerDate(h.hour)?.getTime() ?? NaN, value: h.avg_response_time as number }));

  return withGaps(samples, 1.5 * 3_600_000);
}

export type ResponseSource = 'raw' | 'hourly';

export interface ResponseSeriesInput {
  /** The raw sample for the window -- at most `sampleCap` rows, newest first. */
  checks: Check[];
  /** The window's true check count from the server; `checks` may be a subset. */
  totalInWindow: number;
  /** The `limit` the sample was requested with. */
  sampleCap: number;
  intervalSeconds: number;
  rangeHours: number;
  hourlyStats: CheckHourly[];
}

/**
 * Decide what the response-time panel draws for a window.
 *
 * The checks endpoint caps how many rows it returns and serves the newest
 * first, so for a long window the raw sample is only the tail of it -- a 7d
 * request at a 60s interval came back as the last 16.7h and drew with a "7d"
 * button lit. Draw the hourly rollup (which spans the window) instead, and
 * say so, rather than a shortened axis that looks complete.
 *
 * The decision keys on what the window WOULD hold at this interval, not on
 * what came back: a service added ten hours ago has only 600 checks in its
 * 7d window, all of them under the cap, but its 7d view should look like
 * every other service's 7d view, not a per-minute line crammed into the
 * right sliver of the axis. The truncation check stays as a backstop for
 * anything the estimate misses (manual checks, an interval that changed).
 */
export function selectResponseSeries(input: ResponseSeriesInput): { points: SeriesPoint[]; source: ResponseSource } {
  const { checks, totalInWindow, sampleCap, intervalSeconds, rangeHours, hourlyStats } = input;
  const expectedChecks = (rangeHours * 3600) / Math.max(intervalSeconds, 1);
  if (expectedChecks > sampleCap || totalInWindow > checks.length) {
    return { points: buildHourlyResponseSeries(hourlyStats), source: 'hourly' };
  }
  return { points: buildResponseSeries(checks, intervalSeconds), source: 'raw' };
}

function withGaps(samples: { t: number; value: number }[], gapMs: number): SeriesPoint[] {
  const ordered = [...samples].sort((a, b) => (a.t || 0) - (b.t || 0));
  if (ordered.length === 0) return [];

  const points: SeriesPoint[] = [];
  ordered.forEach((sample, i) => {
    if (i > 0) {
      const previous = ordered[i - 1].t;
      if (sample.t - previous > gapMs) {
        // One null between the two real points breaks the line across the gap.
        points.push({ t: previous + 1, responseTime: null });
      }
    }
    if (!Number.isNaN(sample.t)) points.push({ t: sample.t, responseTime: sample.value });
  });

  return points;
}

export interface UptimePoint {
  t: number;
  uptime: number;
}

export function buildUptimeSeries(stats: CheckHourly[]): UptimePoint[] {
  return stats
    .map(h => ({
      t: parseServerDate(h.hour)?.getTime() ?? NaN,
      uptime: h.check_count > 0 ? Math.round((h.success_count / h.check_count) * 100) : 100,
    }))
    .filter(p => !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);
}

/** `[start, end]` in epoch milliseconds -- the x-axis domain for a chart. */
export type TimeWindow = [number, number];

/**
 * The selected range as an explicit axis domain. Both detail charts pin their
 * x-axis to this rather than to `['dataMin', 'dataMax']`, so a 7d panel is
 * always seven days wide: a service added three days ago draws in the right
 * three-sevenths and the empty left is the truth, not a chart that has
 * quietly become a 3d panel with a 7d button lit. Sharing one window also
 * lines the two panels' axes up with each other.
 */
export function windowBounds(rangeHours: number, end: number = Date.now()): TimeWindow {
  return [end - rangeHours * 3_600_000, end];
}

/** Axis labels that stay readable whether the window is an hour or a month. */
export function timeTickFormatter(rangeMs: number): (t: number) => string {
  const day = 86_400_000;

  if (rangeMs <= 2 * 3_600_000) {
    return t => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (rangeMs <= 2 * day) {
    return t => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return t => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function formatDurationSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(seconds / 86400);
  const h = Math.round((seconds % 86400) / 3600);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/**
 * `error_budget_remaining_minutes` goes negative once a service has consumed
 * more than 100% of its error budget -- exactly the services an availability
 * report exists to surface. `formatDurationSeconds` has no negative branch by
 * design (its other callers -- MTTR, MTBF, outage durations -- are never
 * negative), so this stays a separate helper rather than adding a sign
 * branch to that shared contract. Renders the over-budget case as
 * "over by <duration>" using the magnitude, never a bare negative duration.
 */
export function formatBudgetRemaining(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 0) return `over by ${formatDurationSeconds(-seconds)}`;
  return formatDurationSeconds(seconds);
}
